/* ==========================================================================
   UNIT Solutions | Pro Hub - Video Library
   assets/prohub-video-library.js

   Vanilla JS, no dependencies. Two hard rules learned on this theme:

   1. Never resolve DOM references while the document is still parsing. This
      file is loaded with defer and additionally waits for DOMContentLoaded,
      so every querySelector runs against a complete document.
   2. Motion's page-transition script attaches its own click handler to links
      and sets body.unloading before navigating. An in-page hash link that
      does not navigate therefore leaves the page faded to white. The capture
      phase listener below intercepts same-page hash links first, clears
      body.unloading, and scrolls manually.
   ========================================================================== */

(function () {
  'use strict';

  var EMBED = 'https://www.youtube-nocookie.com/embed/';
  var SCROLL_OFFSET = 120;
  var ROOT_SELECTOR = '[data-prohub-vl]';

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function closestFrom(node, selector) {
    var el = node && node.nodeType === 3 ? node.parentNode : node;
    if (!el || !el.closest) return null;
    return el.closest(selector);
  }

  /* ------------------------------------------------------------------------
     Inline video cards: swap the poster button for an autoplaying embed.
     ------------------------------------------------------------------------ */

  function initCards(root) {
    root.addEventListener('click', function (event) {
      var button = closestFrom(event.target, '[data-prohub-vl-play]');
      if (!button || !root.contains(button)) return;

      var videoId = button.getAttribute('data-video-id');
      if (!videoId) return;

      var frame = button.parentNode;
      if (!frame) return;

      event.preventDefault();

      var iframe = document.createElement('iframe');
      iframe.src = EMBED + encodeURIComponent(videoId) + '?autoplay=1';
      iframe.title = button.getAttribute('data-video-title') || 'Video';
      iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
      iframe.setAttribute('allowfullscreen', 'allowfullscreen');
      iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');

      while (frame.firstChild) frame.removeChild(frame.firstChild);
      frame.appendChild(iframe);

      try { iframe.focus({ preventScroll: true }); } catch (err) { /* older browsers */ }
    });
  }

  /* ------------------------------------------------------------------------
     Unified player: platform tabs plus a playlist rail.

     State mirrors the approved React component exactly:
       - activePlatform defaults to the value in the data island (UNIT9)
       - each platform keeps its own selected video
       - choosing a playlist entry autoplays it
       - switching platforms loads that platform's video without autoplay
     ------------------------------------------------------------------------ */

  function initPlayer(root) {
    var player = root.querySelector('[data-prohub-vl-player]');
    if (!player) return;

    var island = player.querySelector('[data-prohub-vl-data]');
    var stageFrame = player.querySelector('[data-prohub-vl-frame]');
    var rail = player.querySelector('[data-prohub-vl-rail]');
    var panel = player.querySelector('[data-prohub-vl-panel]');
    var tabs = Array.prototype.slice.call(player.querySelectorAll('[data-prohub-vl-tab]'));

    if (!island || !stageFrame || !rail || !tabs.length) return;

    var data;
    try {
      data = JSON.parse(island.textContent || island.innerHTML);
    } catch (err) {
      return;
    }

    var platforms = data.platforms || {};
    var keys = tabs.map(function (tab) { return tab.getAttribute('data-prohub-vl-tab'); });

    var active = data.defaultPlatform;
    if (!platforms[active] || !platforms[active].length) {
      active = null;
      for (var k = 0; k < keys.length; k++) {
        if (platforms[keys[k]] && platforms[keys[k]].length) { active = keys[k]; break; }
      }
    }
    if (!active) return;

    var selected = {};
    keys.forEach(function (key) {
      var list = platforms[key] || [];
      selected[key] = list.length ? list[0].id : '';
    });

    function list() { return platforms[active] || []; }

    function currentVideo() {
      var items = list();
      for (var i = 0; i < items.length; i++) {
        if (items[i].id === selected[active]) return items[i];
      }
      return items[0] || null;
    }

    function paintTabs() {
      tabs.forEach(function (tab) {
        var on = tab.getAttribute('data-prohub-vl-tab') === active;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
        if (on && panel) panel.setAttribute('aria-labelledby', tab.id);
      });
    }

    function buildRail() {
      var fragment = document.createDocumentFragment();
      list().forEach(function (video) {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'prohub-vl__rail-btn';
        button.setAttribute('data-prohub-vl-select', video.id);
        button.setAttribute('aria-pressed', video.id === selected[active] ? 'true' : 'false');
        button.textContent = video.title;
        fragment.appendChild(button);
      });
      while (rail.firstChild) rail.removeChild(rail.firstChild);
      rail.appendChild(fragment);
      rail.setAttribute('aria-label', active + ' reference videos');
    }

    function paintRail() {
      each(rail.querySelectorAll('[data-prohub-vl-select]'), function (button) {
        var on = button.getAttribute('data-prohub-vl-select') === selected[active];
        button.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    function paintStage(autoplay) {
      var video = currentVideo();
      if (!video) return;
      var src = EMBED + encodeURIComponent(video.id) + '?autoplay=' + (autoplay ? '1' : '0');
      if (stageFrame.getAttribute('src') !== src) stageFrame.setAttribute('src', src);
      stageFrame.setAttribute('title', active + ': ' + video.title);
    }

    function setPlatform(key) {
      if (!platforms[key] || key === active) return;
      active = key;
      paintTabs();
      buildRail();
      rail.scrollTop = 0;
      paintStage(false);
    }

    rail.addEventListener('click', function (event) {
      var button = closestFrom(event.target, '[data-prohub-vl-select]');
      if (!button || !rail.contains(button)) return;
      var id = button.getAttribute('data-prohub-vl-select');
      if (!id || id === selected[active]) return;
      selected[active] = id;
      paintRail();
      paintStage(true);
    });

    tabs.forEach(function (tab, index) {
      tab.addEventListener('click', function () {
        setPlatform(tab.getAttribute('data-prohub-vl-tab'));
      });

      tab.addEventListener('keydown', function (event) {
        var next = -1;
        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            next = (index + 1) % tabs.length;
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            next = (index - 1 + tabs.length) % tabs.length;
            break;
          case 'Home':
            next = 0;
            break;
          case 'End':
            next = tabs.length - 1;
            break;
          default:
            return;
        }
        event.preventDefault();
        tabs[next].focus();
        setPlatform(tabs[next].getAttribute('data-prohub-vl-tab'));
      });
    });

    paintTabs();
    buildRail();
    paintStage(false);
  }

  /* ------------------------------------------------------------------------
     Rich text links: restore rel on new-tab links, since Shopify's rich text
     sanitizer drops the rel attribute from schema defaults.
     ------------------------------------------------------------------------ */

  function hardenLinks(root) {
    each(root.querySelectorAll('a[target="_blank"]'), function (link) {
      var rel = (link.getAttribute('rel') || '').toLowerCase();
      if (rel.indexOf('noopener') === -1 || rel.indexOf('noreferrer') === -1) {
        link.setAttribute('rel', 'noopener noreferrer');
      }
    });
  }

  /* ------------------------------------------------------------------------
     Same-page hash links: handle before Motion's transition handler.
     ------------------------------------------------------------------------ */

  var anchorGuardBound = false;

  function bindAnchorGuard() {
    if (anchorGuardBound) return;
    anchorGuardBound = true;

    document.addEventListener('click', function (event) {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      var link = closestFrom(event.target, 'a[href]');
      if (!link || !link.closest(ROOT_SELECTOR)) return;

      var raw = link.getAttribute('href') || '';
      var hash = '';

      if (raw.charAt(0) === '#') {
        hash = raw.slice(1);
      } else {
        var url;
        try {
          url = new URL(link.href, window.location.href);
        } catch (err) {
          return;
        }
        if (url.origin !== window.location.origin) return;
        if (url.pathname !== window.location.pathname) return;
        if (url.search !== window.location.search) return;
        if (!url.hash) return;
        hash = url.hash.slice(1);
      }

      if (!hash) return;

      var target = document.getElementById(hash);
      if (!target) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      document.body.classList.remove('unloading');

      var top = target.getBoundingClientRect().top + (window.pageYOffset || document.documentElement.scrollTop) - SCROLL_OFFSET;
      if (top < 0) top = 0;

      try {
        window.scrollTo({ top: top, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      } catch (err) {
        window.scrollTo(0, top);
      }

      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, '', '#' + hash);
      }

      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      try { target.focus({ preventScroll: true }); } catch (err) { /* older browsers */ }
    }, true);
  }

  /* ------------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------------ */

  function initRoot(root) {
    if (!root || root.hasAttribute('data-prohub-vl-ready')) return;
    root.setAttribute('data-prohub-vl-ready', 'true');
    hardenLinks(root);
    initCards(root);
    initPlayer(root);
  }

  function boot() {
    bindAnchorGuard();
    each(document.querySelectorAll(ROOT_SELECTOR), initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme editor re-renders the section without reloading the page. */
  document.addEventListener('shopify:section:load', function (event) {
    var scope = event.target;
    if (!scope || !scope.querySelectorAll) return;
    bindAnchorGuard();
    each(scope.querySelectorAll(ROOT_SELECTOR), initRoot);
  });
})();
